import {Communication} from "types/communication"
import PendingMessages from "./pendingMessages"

// eslint-disable-next-line
let status: Communication.SocketStatus = Communication.SocketStatus.Unknown
const callbacks: {[key: string]: any} = []
const RECONNECT_BASE = 1 * 1000
const RECONNECT_MAX = 30 * 1000
let reconnectTimer: any = null
let reconnectAttempts = 0
let hasConnectedBefore = false
let unauthorizedReceived = false
// Consecutive "unauthorized" closes without a successful auth; we stop retrying after MAX_UNAUTHORIZED_ATTEMPTS
let unauthorizedAttempts = 0
const MAX_UNAUTHORIZED_ATTEMPTS = 3
// Fallback in case the server stays silent after accepting auth
const AUTH_ACCEPTED_FALLBACK = 3 * 1000
let socketUnsubcribe: any = null
const reconnectedListeners = new Set<() => void>()

const WS_API_CHAT_URL = process.env.REACT_APP_CHAT_API_V2_BASE_URL
const WEBSOCKET_PROTOCOL = process.env.REACT_APP_WEBSOCKET_PROTOCOL
let heartBeatId = null

let domain = WS_API_CHAT_URL.split("//")[1]
let ws: WebSocket

type InitializeOptions = {
    // Called when the server rejects the token; should refresh the session so the next getParams() has a valid one
    onUnauthorized?: () => Promise<void> | void
}

// Exponential backoff with full jitter: 1s, 2s, 4s ... capped at 30s
const getReconnectDelay = () => {
    const ceiling = Math.min(RECONNECT_MAX, RECONNECT_BASE * 2 ** reconnectAttempts)
    return Math.round(ceiling / 2 + Math.random() * (ceiling / 2))
}

/**
 * Init websocket and listeners.
 * @param getParams called on every (re)connection so the current token is always used
 */
const initialize = (getParams: () => any, options: InitializeOptions = {}) => {
    let disposed = false
    clearTimeout(reconnectTimer)
    ws = new WebSocket(`${WEBSOCKET_PROTOCOL}${domain}/ws`)
    const socket = ws
    let authAccepted = false

    // Called once the server did not reject the auth packet (first non-"unauthorized" message, or after a short
    // silence). Only then it is worth refreshing UI state after a reconnection.
    // `confirmed` is true only when the server actually answered; the silence fallback refreshes the UI but must not
    // reset the retry counters, otherwise a late rejection would never reach the retry limit.
    const onAuthAccepted = (confirmed: boolean = true) => {
        if (disposed || socket !== ws || unauthorizedReceived) return
        if (socket.readyState !== WebSocket.OPEN) return
        if (confirmed) {
            reconnectAttempts = 0
            unauthorizedAttempts = 0
        }
        if (authAccepted) return
        authAccepted = true
        if (hasConnectedBefore) {
            // WS has no replay: let the UI refresh what it may have missed while offline
            reconnectedListeners.forEach((listener) => listener())
        }
        hasConnectedBefore = true
    }

    ws.onopen = () => {
        const packet = {
            command: Communication.SocketCommand.Auth,
            payload: getParams()
        }
        try {
            setStatus(Communication.SocketStatus.Connected)
            socket.send(JSON.stringify(packet))
            unauthorizedReceived = false
            heartBeat()
            setTimeout(() => onAuthAccepted(false), AUTH_ACCEPTED_FALLBACK)
        } catch (error) {}
    }

    ws.onmessage = (event: any) => {
        const dataObj = JSON.parse(event.data)
        const {command, data} = dataObj
        if (command === "unauthorized") {
            // Server closes with code 1000 right after this: it is not a voluntary close
            unauthorizedReceived = true
        } else {
            onAuthAccepted()
        }
        if (command === Communication.SocketCommand.NewMessage) {
            if (data?.clientMessageId) {
                PendingMessages.resolve(data.clientMessageId)
            }
            notify("messages", data)
            notify("sidebar-messages", data)
            const {roomId} = data
            notify(`message-${roomId}`, data)
        }
        if (command === Communication.SocketCommand.CommandSendError) {
            // Only newMessage failures are tracked; other commands are silent for now
            if (data?.command === Communication.SocketCommand.NewMessage && data?.clientMessageId) {
                PendingMessages.fail(data.clientMessageId, data.message)
            }
        }
        if (command === Communication.SocketCommand.Typing) {
            notify("typings", data)
            const {roomId} = data
            notify(`typing-${roomId}`, data)
        }
        if (command === "reaction") {
            const {roomId} = data
            notify(`reaction-${roomId}`, data)
        }
        if (command === Communication.SocketCommand.EditMessage) {
            const {roomId} = data
            notify(`updated-message-${roomId}`, data)
        }
        if (command === Communication.SocketCommand.RoomCreated) {
            notify(`room-created`, data)
        }
        if (command === Communication.SocketCommand.RoomUnread) {
            notify(`room-unread`, data)
            notify(`thread-unread`, data)
        }
        if (command === Communication.SocketCommand.TotalUnread) {
            notify(`total-unread`, data)
        }
        if (command === Communication.SocketCommand.RoomUpdated) {
            notify(`room-updated`, data)
        }
        if (command === Communication.SocketCommand.RoomRemoved) {
            notify(`room-removed`, data)
        }
        if (command === Communication.SocketCommand.NewNotification) {
            notify("sidebar-notifications", data)
            notify(`new-notification`, data)
        }
        if (command === Communication.SocketCommand.UpdateMessageReadStatus) {
            const {roomId} = data
            notify(`updated-message-read-${roomId}`, data)
        }
        if (command === Communication.SocketCommand.ThreadCreated) {
            notify(`scheduled-thread-created`, data)
            notify(`sidebar-thread-created`, data)
            const {parentRoomId} = data
            notify(`thread-created-${parentRoomId}`, data)
        }
        if (
            [
                Communication.SocketCommand.UpdatePoll,
                Communication.SocketCommand.PollClosed,
                Communication.SocketCommand.UpdatePollVoters
            ].includes(command)
        ) {
            const {roomId} = data
            notify(`poll-${roomId}`, data)
        }
        if (command === Communication.SocketCommand.UpdateMessagePinned) {
            const {messageId, roomId} = data
            notify(`message-pin-status-${messageId}`, data)
            notify(`room-pin-message-${roomId}`, data)
        }
        if (command === Communication.SocketCommand.OnlineStatus) {
            notify(`room-user-online`, data)
        }
        if (command === Communication.SocketCommand.SupportTicketUpdated) {
            const {support} = data
            notify(`sidebar-support-updated`, support)
            notify(`support-updated-${support.roomId}`, support)
        }
        if (command === Communication.SocketCommand.UpdateMessageSmsDelivery) {
            const {roomId} = data
            notify(`message-sms-delivery-${roomId}`, data)
        }
    }

    ws.onclose = (error) => {
        // Ignore events of stale sockets
        if (disposed || socket !== ws) return
        setStatus(Communication.SocketStatus.Disconnected)
        clearTimeout(heartBeatId)
        if (error.code !== 1000 || unauthorizedReceived) {
            const wasUnauthorized = unauthorizedReceived
            if (wasUnauthorized) {
                unauthorizedAttempts++
                if (unauthorizedAttempts > MAX_UNAUTHORIZED_ATTEMPTS) {
                    // Expired or logged-out session: stop hammering the auth endpoint and the server
                    return
                }
            }
            const delay = getReconnectDelay()
            reconnectAttempts++
            clearTimeout(reconnectTimer)
            reconnectTimer = setTimeout(async () => {
                if (disposed) return
                if (wasUnauthorized) {
                    try {
                        await options.onUnauthorized?.()
                    } catch (e) {
                        // Session could not be refreshed: do not reconnect with the same invalid token
                        return
                    }
                    if (disposed) return
                }
                if (!getParams()?.token) return
                // initialize() registers the teardown of the newest socket in socketUnsubcribe
                initialize(getParams, options)
            }, delay)
        }
    }

    ws.onerror = (_error) => {
        // onclose fires afterwards and schedules the reconnection
        socket.close()
        setStatus(Communication.SocketStatus.Unknown)
    }

    socketUnsubcribe = () => {
        disposed = true
        clearTimeout(reconnectTimer)
        reconnectAttempts = 0
        hasConnectedBefore = false
        unauthorizedReceived = false
        unauthorizedAttempts = 0
        socket.close(1000, "done")
        // Remove all listeners
        Object.keys(callbacks).forEach((key) => {
            callbacks[key] = undefined
        })
        clearTimeout(heartBeatId)
    }

    function heartBeat() {
        if (status === "connected") {
            clearTimeout(heartBeatId)
            const packet = {
                command: Communication.SocketCommand.Ping,
                payload: {}
            }
            ws.send(JSON.stringify(packet))
            heartBeatId = setTimeout(heartBeat, 30000)
        }
    }

    return () => {
        if (socketUnsubcribe) {
            socketUnsubcribe()
        }
    }
}

/**
 * @returns true when the packet was written to an open socket, false when it was dropped
 */
function sendPacket(data): boolean {
    if (status === "connected" && ws && ws.readyState === WebSocket.OPEN) {
        try {
            ws.send(JSON.stringify(data))
            return true
        } catch (e) {
            return false
        }
    }
    return false
}

const registerOnReconnected = (callback: () => void) => {
    reconnectedListeners.add(callback)
    return () => {
        reconnectedListeners.delete(callback)
    }
}
const setStatus = (wsStatus: Communication.SocketStatus) => {
    status = wsStatus
    // const listener = callbacks.stateChange;
    // if (listener) {
    //   listener(wsStatus);
    // }
}

// const registerOnStateChange = (
//   callback: OnStateChangeCallback,
// ): (() => void) => {
//   callbacks.stateChange = callback;
//   callback(status);
//   return () => {
//     callbacks.stateChange = undefined;
//   };
// };

const registerOnNewMessage = (roomId: number | string, callback: (message: Communication.Message) => void) =>
    registerListener(`message-${roomId}`, callback)

const registerOnNewMessages = (callback: (message: any) => void) => registerListener("messages", callback)
const registerOnSidebarNewMessages = (callback: (message: any) => void) =>
    registerListener("sidebar-messages", callback)

const registerOnRoomUnread = (callback: (data: any) => void) => registerListener("room-unread", callback)
const registerOnThreadUnread = (callback: (data: any) => void) => registerListener("thread-unread", callback)
const registerOnTotalUnread = (callback: (data: any) => void) => registerListener("total-unread", callback)
const registerOnRoomUpdated = (callback: (data: any) => void) => registerListener("room-updated", callback)

const registerTyingEvent = (roomId: number, callback: (value: Communication.TypingEvent) => void) =>
    registerListener(`typing-${roomId}`, callback)

const registerTypingsEvent = (callback: (value: Communication.TypingEvent) => void) =>
    registerListener("typings", callback)

const registerOnUpdatedMessage = (roomId: number | string, callback: (data: any) => void) =>
    registerListener(`updated-message-${roomId}`, callback)

const registerOnUpdatedMessageReadStatus = (roomId: number | string, callback: (data: any) => void) =>
    registerListener(`updated-message-read-${roomId}`, callback)

const registerOnMessagePinStatus = (messageId: number, callback: (data: any) => void) =>
    registerListener(`message-pin-status-${messageId}`, callback)

const registerOnRoomPinMessage = (roomId: number | string, callback: (data: any) => void) =>
    registerListener(`room-pin-message-${roomId}`, callback)

const registerOnRoomCreated = (callback: (data: any) => void) => registerListener(`room-created`, callback)

const registerOnRoomRemoved = (callback: (data: any) => void) => registerListener(`room-removed`, callback)

const registerOnNewNotification = (callback: (data: any) => void) => registerListener(`new-notification`, callback)
const registerOnSidebarNewNotification = (callback: (data: any) => void) =>
    registerListener(`sidebar-notifications`, callback)

const registerOnScheduledThreadCreated = (callback: (data: any) => void) =>
    registerListener(`scheduled-thread-created`, callback)

const registerOnSidebarThreadCreated = (callback: (data: any) => void) =>
    registerListener(`sidebar-thread-created`, callback)

const registerOnThreadCreated = (roomId: number | string, callback: (data: any) => void) =>
    registerListener(`thread-created-${roomId}`, callback)

const registerOnUpdatePoll = (roomId: number | string, callback: (data: any) => void) =>
    registerListener(`poll-${roomId}`, callback)

const registerOnRoomUserOnline = (callback: (data: any) => void) => registerListener(`room-user-online`, callback)

const registerOnSidebarSupportUpdated = (callback: (data: any) => void) =>
    registerListener(`sidebar-support-updated`, callback)

const registerOnSupportUpdated = (roomId: number | string, callback: (data: any) => void) =>
    registerListener(`support-updated-${roomId}`, callback)

const registerOnUpdateMessageSmsDelivery = (roomId: number | string, callback: (data: any) => void) =>
    registerListener(`message-sms-delivery-${roomId}`, callback)

const registerListener = (name: string, callback: any) => {
    callbacks[name] = callback
    return () => {
        callbacks[name] = null
    }
}

const notify = (name: string, data: any) => {
    const listener = callbacks[name]

    if (listener) {
        listener(data)
    }
}

const ChatWsV2 = {
    initialize,
    registerOnNewMessage,
    registerOnNewMessages,
    registerOnSidebarNewMessages,
    registerTyingEvent,
    registerTypingsEvent,
    registerOnUpdatedMessage,
    registerOnRoomCreated,
    registerOnRoomRemoved,
    sendPacket,
    registerOnReconnected,
    registerOnRoomUnread,
    registerOnThreadUnread,
    registerOnTotalUnread,
    registerOnRoomUpdated,
    registerOnMessagePinStatus,
    registerOnUpdatedMessageReadStatus,
    registerOnScheduledThreadCreated,
    registerOnSidebarThreadCreated,
    registerOnThreadCreated,
    registerOnUpdatePoll,
    registerOnNewNotification,
    registerOnSidebarNewNotification,
    registerOnRoomPinMessage,
    registerOnRoomUserOnline,
    registerOnSidebarSupportUpdated,
    registerOnSupportUpdated,
    registerOnUpdateMessageSmsDelivery
}

export default ChatWsV2
